import { EvtEmployeeCard } from "components/employee/display-card";
import { useParams } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { fetchReceivedReview } from "./api";
import { CircleProgress } from "@/modules/performance/feedbacks/feedback/components/progress";
import { avgOfKeys } from "@/modules/performance/feedbacks/feedback/utils";
import { Card } from "ui/components/card";
import { ScoreItem } from "@/modules/performance/feedbacks/feedback/components/score-item";
import { sections } from "@/modules/performance/feedbacks/feedback/constants";
import { DescriptionItem } from "@/modules/performance/feedbacks/feedback/components/description-item";
import EmployeeSection from "@/modules/performance/components/section/employee";
import DashboardMenu from "@/components/menu";
import { PermissionControlledInput } from "permissions/components/input";
import EditItemButton from "components/buttons/edit";
import { DropdownMenuItem } from "ui/components/dropdown-menu";
import { useQuad } from "@/modules/performance/context";
import { PerformanceFeedbackLoader } from "./loader";

export default function PerformanceFeedback({
  showReviewOf = false,
  showReviewBy = false,
  showEdit = false,
}) {
  const { reviewId } = useParams();
  const { isQuadCompleted } = useQuad();

  const { data, isLoading, isError } = useFetch({
    queryKey: ["received-review", reviewId],
    queryFn: () => fetchReceivedReview({ reviewId }),
  });

  if (isLoading) {
    return (
      <PerformanceFeedbackLoader
        showReviewOf={showReviewOf}
        showReviewBy={showReviewBy}
      />
    );
  }

  if (isError) {
    return (
      <div className="flex-1 m-6 flex items-center justify-center">
        <Card className="p-8 text-center space-y-4">
          <div className="text-destructive text-lg font-semibold">
            Error Loading Performance Review
          </div>
          <p className="text-muted-foreground">
            Unable to fetch the performance review data. Please try refreshing
            the page.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 m-6 space-y-6">
      {/* {showEdit && (
        <DashboardMenu>
          <DropdownMenuItem>
            <PermissionControlledInput
              rule="update:PC,SC"
              collection="projects"
            >
              <EditItemButton disabled={isQuadCompleted()} />
            </PermissionControlledInput>
          </DropdownMenuItem>
        </DashboardMenu>
      )} */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {showReviewOf && (
          <EmployeeSection title="Review of" employee={data.reviewee} />
        )}
        {showReviewBy && (
          <EmployeeSection title="Review by" employee={data.reviewer} />
        )}
      </div>
      <div className="space-y-3">
        {sections.map((section) => {
          const percent = avgOfKeys(
            data,
            section.items.map((i) => i.key),
          );
          return (
            <div key={section.title} className="space-y-3">
              <h2 className="text-lg font-semibold mb-2">{section.title}</h2>
              <Card className="flex !flex-row p-4 gap-4 items-start shadow-none">
                <CircleProgress value={percent} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 flex-1">
                  {section.items.map(({ key, label }) => (
                    <ScoreItem key={key} label={label} score={data[key]} />
                  ))}
                </div>
              </Card>
            </div>
          );
        })}
      </div>
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Detailed Feedback</h2>
        <div className="grid grid-cols-1 md:grid-cols-2  gap-4">
          <DescriptionItem title="Strengths" text={data.strengths} />
          <DescriptionItem
            title="Areas for Improvement"
            text={data.improvements}
          />
          <DescriptionItem
            title="Work Quality and Technical Skills"
            text={data.work_quality_and_technical_skills}
          />
        </div>
      </div>
    </div>
  );
}
